import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useCart } from '../lib/CartContext';

const ProductCard = ({ product }) => {
  const { addItem, openDrawer } = useCart();
  const isSplitPoster = product.category === 'Split Posters';
  // Real shape of the image: saved size first, otherwise measured when it loads.
  const [measured, setMeasured] = useState(null);
  const w = product.image_width || measured?.w;
  const h = product.image_height || measured?.h;
  const ratio = w && h ? w / h : isSplitPoster ? 16 / 9 : 3 / 4;
  const aspectRatio = w && h ? `${w} / ${h}` : isSplitPoster ? '16 / 9' : '3 / 4';
  // Wide posters (split posters, landscape art) take two columns so the whole
  // poster is visible at a decent size.
  const isWide = ratio > 1.15;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const sizes = product.sizes || [];
    if (sizes.length === 0) {
      toast.error('This product has no sizes set up yet.');
      return;
    }
    const cheapest = [...sizes].sort((a, b) => a.price - b.price)[0];
    addItem(product, cheapest, 1);
    toast.success(`${product.name} (${cheapest.label}) added to cart`, {
      action: { label: 'View cart', onClick: openDrawer },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -8 }}
      className={`group relative ${isWide ? 'col-span-2' : ''}`}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        <div
          className="relative overflow-hidden bg-secondary border border-border/40 rounded-sm"
          style={{ aspectRatio }}
        >
          <motion.img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
            onLoad={(e) => {
              const img = e.currentTarget;
              if (!product.image_width && img.naturalWidth) {
                setMeasured({ w: img.naturalWidth, h: img.naturalHeight });
              }
            }}
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {product.trending && (
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute top-3 left-3 bg-white text-black text-xs font-medium px-3 py-1 rounded-sm"
            >
              TRENDING
            </motion.div>
          )}
          {product.limited_edition && (
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute top-3 right-3 bg-red-600 text-white text-xs font-medium px-3 py-1 rounded-sm"
            >
              LIMITED
            </motion.div>
          )}
          
          <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleQuickAdd}
              className="w-full bg-white text-black py-2 px-4 rounded-sm font-medium flex items-center justify-center gap-2 hover:bg-white/90 transition-colors"
              data-testid={`quick-buy-button-${product.id}`}
            >
              <ShoppingBag size={18} weight="bold" />
              Add to Cart
            </motion.button>
          </div>
        </div>
        
        <motion.div
          className="mt-4 space-y-1"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-foreground font-medium text-base line-clamp-1">{product.name}</h3>
          <p className="text-foreground font-heading font-bold text-lg">
            {product.sizes && product.sizes.length > 1 ? (
              <>
                <span className="text-muted-foreground text-xs font-normal mr-1">from</span>
                ₹{Math.min(...product.sizes.map((s) => s.price))}
              </>
            ) : (
              <>₹{product.price}</>
            )}
          </p>
        </motion.div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
